import { createElement, uniqueId } from "./_dom";
import { initPopupContainer } from "./_menu";
import { registerComponent } from "./component";
import * as Res from "./res";

class Modal {
	constructor(content, options) {
		this.content = content;
		this.options = { id: uniqueId("modal-dialog"), appendTo: "body", ...options };
		this.open = false;
	}

	get element() {
		return document.getElementById(this.options.id);
	}

	get backdrop() {
		return this.#backdrop;
	}

	get closeButton() {
		return this.element.querySelector('button.v3-dialog__button-close');
	}

	addModal() {
		const { noclose, nobackdrop, appendTo, ...attributes } = this.options;
		const modal = createElement('div', {
				tabindex: '-1',
				role: 'dialog',
				class: 'v3-dialog',
				...attributes
			},
			!noclose && createElement('button', {
					class: 'v3-dialog__button-close v3-button ui-icon-close ui-icon-only',
					type: 'button',
					onclick: this.handler
				},
				createElement("span", { class: "ui-icontext" }, Res.getString("action.close"))
			),
			// content might be a fragment, classList will be undefined
			this.content.classList?.contains("v3-dialog__content") ? this.content : createElement('div', { class: "v3-dialog__content" }, this.content)
		);
		let elementToAppendTo;
		switch (appendTo) {
			case "auto":
				elementToAppendTo = initPopupContainer();
				break;
			case "body":
				elementToAppendTo = document.body;
				break;
			default:
				elementToAppendTo = appendTo;
				break;
		}
		elementToAppendTo.append(modal);

		if (!nobackdrop) {
			const overlay = createElement('div', { class: 'v3-dialog__backdrop' });
			if (!noclose)
				overlay.addEventListener("click", this.clickOutside);
			modal.before(overlay);
			this.#backdrop = overlay;
		}
		if (!noclose) {
			modal.addEventListener("keydown", event => {
				if (event.key === "Escape") {
					this.close();
					event.stopPropagation();
				}
			});
		}
	}

	removeModal() {
		document.body.style.overflow = "";
		this.element.remove();
		this.#backdrop?.remove();
		this.#backdrop = undefined;
	}

	clickOutside = ({ target }) => {
		const isOutside = !target.closest('.v3-dialog');
		if (isOutside) {
			this.element.classList.remove('show');
			this.removeModal();
		}
	};

	close() {
		if (this.open) {
			this.element.classList.remove('show');
			this.removeModal();
		}
	}

	handler = () => {
		if (this.open) {
			this.element.classList.remove('show');
			this.removeModal();
		} else {
			document.body.style.overflow = 'hidden';
			this.addModal();
			this.element.classList.add('show');
		}

		this.open = !this.open;
	};

	start() {
		this.handler();
		return this;
	}

	/** @type {HTMLElement|DocumentFragment} */
	content;

	/** @type {HTMLElement|undefined} */
	#backdrop
}

function createModal(content, options) {
	return new Modal(content, options);
}

const modal = registerComponent(createModal);

export { modal, Modal };
